import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/common/BreadCrumb";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import RippleLoader from "../../components/RippleLoader/RippleLoader";
import {
  DefaultButton,
  DefaultPrimaryButton,
  InputWithLabel,
  SelectWithLabel,
} from "../../components/micro/input";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { useDispatch, useSelector } from "react-redux";
import { companiesList, getOutlet } from "../../redux/companies/slice";
import {
  createService,
  getServiceById,
  getTechnicianList,
} from "../../redux/service/slice";
import { getStaffsList } from "../../redux/staffs/slice";
import { Chip, IconButton } from "@mui/material";
import { Cancel } from "@mui/icons-material";
import { ClearIcon } from "@mui/x-date-pickers";

const CreateEditRecurringService = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isEditMode = location?.state?.mode;
  const companyData = location?.state?.companyData;
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const serviceData =
    isEditMode === "Renew"
      ? location?.state?.renewData
      : location?.state?.service;

  // Selector
  const {
    outletList,
    techniciansList,
    companiesListData,
    staffsList,
    getServiceListData,
  } = useSelector((store) => ({
    outletList: store?.companiesData?.getOutletStatus?.data?.data,
    techniciansList: store?.technicianData?.getTechnicianListStatus?.data,
    companiesListData: store?.companiesData?.companiesListStatus?.data?.data,
    staffsList: store?.staffsData?.getStaffsListStatus?.data,
    // get service
    getServiceListData: store?.serviceData?.getServiceByIdStatus?.data?.data,
  }));

  useEffect(() => {
    if (serviceData?.id) {
      dispatch(getServiceById(serviceData?.id));
    }
    dispatch(
      getOutlet({
        search: "",
        pageLimit: "all",
        company_id: companyData?.id || "",
        sort_by: "name",
        sort_direction: "asc",
      })
    );
    dispatch(
      getTechnicianList({
        pageLimit: "all",
      })
    );
    dispatch(
      companiesList({
        search: "",
        pageLimit: "all",
        status: "active",
        sort_by: "name",
        sort_direction: "desc",
        contract: "all",
      })
    );
    dispatch(
      getStaffsList({
        search: "",
        pageLimit: "all",
        sort_by: "name",
        sort_direction: "desc",
      })
    );
  }, []);

  const contractData = getServiceListData?.data?.[0];

  const techniciansOptions = Array.isArray(techniciansList?.data)
    ? techniciansList.data.map((technician) => ({
        value: technician.id,
        label: technician.full_name,
      }))
    : Array.isArray(techniciansList)
    ? techniciansList.map((technician) => ({
        value: technician.id,
        label: technician.full_name,
      }))
    : [];

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [outletOptions, setOutletOptions] = useState([]);
  const [companyKey, setCompanyKey] = useState(0);

  // Company options
  const companyOptions = Array.isArray(
    companiesListData?.data || companiesListData
  )
    ? (companiesListData?.data || companiesListData).map((company) => ({
        value: company?.id,
        label: company?.name,
      }))
    : [];

  // Filter customer options based on selected company
  useEffect(() => {
    if (selectedCompany) {
      const filteredCustomers = Array.isArray(staffsList?.data)
        ? staffsList.data
            .filter((customer) => customer.company_id === selectedCompany)
            .map((customer) => ({
              value: customer.id,
              label: customer.full_name,
            }))
        : [];
      setCustomerOptions(filteredCustomers);
    }
  }, [companyKey, staffsList]);

  // Filter outlet options based on selected company
  useEffect(() => {
    if (selectedCompany) {
      const filteredOutlets = Array.isArray(outletList)
        ? outletList
            .filter((outlet) => outlet.company_id === selectedCompany)
            .map((outlet) => ({
              value: outlet.id,
              label: outlet.name,
            }))
        : [];
      setOutletOptions(filteredOutlets);
    }
  }, [companyKey, outletList]);

  // On Edit, set the selected company and customer
  useEffect(() => {
    if (serviceData?.id || contractData?.company || companyData) {
      setSelectedCompany(contractData?.company?.id || companyData?.id);
    }
  }, [serviceData?.id, contractData?.company?.id]);

  const handleCompanyChange = (companyId) => {
    setSelectedCompany(companyId);
    setCompanyKey(Date.now());
  };

  const itemOptions = [
    { value: "1 month", label: "1 Month" },
    { value: "2 months", label: "2 Months" },
    { value: "3 months", label: "3 Months" },
    { value: "4 months", label: "4 Months" },
    { value: "6 months", label: "6 Months" },
    { value: "12 months", label: "12 Months" },
  ];

  const handleAddClick = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirm = async (values, resetForm) => {
    setIsLoading(true);
    const payload = {
      servicing_request_id: 2,
      service_quotation_id: "",
      sales_id: "",
      company_id: values?.companyName?.value || values.companyName,
      customer_id: values?.customerFullName?.value || values?.customerFullName,
      outlet_ids: Array.isArray(values?.outlet)
        ? values.outlet.map((outlet) => outlet?.value)
        : [],
      technician_ids: Array.isArray(values?.technician)
        ? values.technician.map((tech) => tech?.value)
        : [],
      contactNumber: values?.contactNumber,
      email: values?.email,
      remark: values?.remark,
      request_types: [
        {
          items: [
            {
              cycle_duration: values?.duration?.value || values?.duration,
              quantity: values?.quantity,
              price: values?.pricing,
            },
          ],
        },
      ],
    };

    const action = serviceData?.id
      ? createService({
          id: serviceData.id,
          ...payload,
          ...(isEditMode === "duplicate" ? { duplicate: serviceData.id } : {}),
        })
      : createService(payload);

    setIsLoading(true);
    try {
      const res = await dispatch(action);
      if (res.type === "createService/fulfilled") {
        navigate(-1);
        resetForm();
      }
    } catch (error) {
      console.error("Error while creating service:", error);
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
    }
  };

  const handleDeleteOutlet = (e, value, values, setFieldValue) => {
    e.stopPropagation();

    const updatedOutlets = values?.outlet?.filter(
      (outlet) => outlet.value !== value
    );

    setFieldValue("outlet", updatedOutlets);
    
    // Commented out: Auto-populate remark with selected outlets
    // if (updatedOutlets.length > 0) {
    //   const removedOutlet = values?.outlet?.find(outlet => outlet.value === value);
    //   const removedOutletName = removedOutlet?.label || "";
    //   
    //   const currentRemark = values.remark || "";
    //   const lines = currentRemark.split("\n");
    //   const updatedLines = lines.filter(line => {
    //     const lineText = line.trim();
    //     return !lineText.startsWith(removedOutletName + " -");
    //   });
    //   
    //   if (updatedLines.length === 0 || updatedLines.every(line => !line.trim())) {
    //     setFieldValue("remark", "");
    //   } else {
    //     const outletText = updatedOutlets
    //       .map((option) => `${option.label} - `)
    //       .join("\n");
    //     setFieldValue("remark", outletText);
    //   }
    // } else {
    //   setFieldValue("remark", "");
    // }
  };

  const handleDeleteTechnician = (e, value, values, setFieldValue) => {
    e.stopPropagation();

    const updatedTechnicians = values?.technician?.filter(
      (technician) => technician.value !== value
    );

    setFieldValue("technician", updatedTechnicians);
  };

  return (
    <>
      <div>
        <div className="flex justify-between items-start">
          <div>
            <h6 className="font-Montserrat font-medium text-xl mb-3">
              {isEditMode === "duplicate"
                ? "Duplicate Service Request"
                : isEditMode === "edit"
                ? "Edit Service Request"
                : isEditMode === "Renew"
                ? "Renew Service Request"
                : "Create Service Request"}
            </h6>
            <Breadcrumb
              breadCrumbParent={"Home"}
              breadCrumbActive={
                isEditMode === "Renew"
                  ? "Renew Contract Service Request"
                  : "Create Service Request"
              }
              breadCrumbParent2={"Service Request"}
              breadCrumbTitleKey={"/"}
              classes=""
            />
          </div>
        </div>
        {isLoading ? (
          <RippleLoader />
        ) : (
          <Formik
            enableReinitialize
            initialValues={{
              companyName:
                serviceData?.id || isEditMode === "companyRecurring"
                  ? {
                      value: contractData?.company?.id || companyData?.id || "",
                      label:
                        contractData?.company?.name || companyData?.name || "",
                    }
                  : null,
              customerFullName: serviceData?.id
                ? {
                    value: contractData?.customers?.id || "",
                    label: contractData?.customers?.full_name || "",
                  }
                : null,
              outlet: serviceData?.id
                ? serviceData?.outlets?.map((outlet) => ({
                    value: outlet?.outlet?.id || "",
                    label: outlet?.outlet?.name || "",
                  }))
                : [],

              technician: serviceData?.id
                ? serviceData?.technicians?.map((technician) => ({
                    value: technician?.id || "",
                    label: technician?.full_name || "",
                  }))
                : [],
              contactNumber: serviceData?.id ? contractData?.phone || "" : "",
              email: serviceData?.id ? contractData?.email || "" : "",
              remark: serviceData?.id ? contractData?.remarks || "" : "",
              duration: serviceData?.id
                ? contractData?.service_requests?.[0]
                    ?.service_request_items?.[0]?.cycle_duration || ""
                : "",
              quantity: serviceData?.id
                ? contractData?.service_requests?.[0]
                    ?.service_request_items?.[0]?.quantity || ""
                : "",
              pricing: serviceData?.id
                ? contractData?.service_requests?.[0]
                    ?.service_request_items?.[0]?.price || ""
                : "",
            }}
            validationSchema={Yup.object().shape({
              companyName: Yup.object().required("Please select company name"),
              customerFullName: Yup.object().required(
                "Please select customer fullname"
              ),
              quantity: Yup.string().required("Please enter quantity"),
              pricing: Yup.string().required("Please enter price"),
            })}
            onSubmit={(values, { setSubmitting }) => {
              setFormValues(values);
              handleAddClick();
              setSubmitting(false);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              setTouched,
              setFieldValue,
            }) => (
              <Form>
                <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mt-5">
                  <div>
                    <p className="text-xl mb-4">
                      {isEditMode === "duplicate"
                        ? "Duplicate Service Request"
                        : isEditMode === "edit"
                        ? "Edit Service Request"
                        : isEditMode === "Renew"
                        ? "Renew Contract Service Request"
                        : "New Service Request"}
                    </p>
                    <h2 className="text-base pb-6 font-normal text-textSecondary">
                      Basic Information
                    </h2>
                  </div>
                  <div className="grid grid-cols-2 gap-6 mb-4">
                    {/* Company name */}
                    <SelectWithLabel
                      idFromName="companyName"
                      placeholder="Company Name*"
                      options={companyOptions}
                      value={values?.companyName?.value || ""}
                      setFieldValue={setFieldValue}
                      errors={errors.companyName}
                      touched={touched.companyName}
                      disabled={isEditMode === "companyRecurring"}
                      endAdornment={
                        values?.companyName ? (
                          <IconButton
                            disabled={isEditMode === "companyRecurring"}
                            sx={{ position: "relative", right: "10px" }}
                            onClick={() => {
                              setFieldValue("companyName", "");
                              setFieldValue("customerFullName", "");
                              setFieldValue("outlet", []);
                              setCustomerOptions([]);
                              setOutletOptions([]);
                            }}
                          >
                            <ClearIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        ) : null
                      }
                      onChange={(event) => {
                        const selectedOption = companyOptions.find(
                          (option) => option.value === event.target.value
                        );

                        if (!selectedOption) return;

                        setFieldValue("companyName", selectedOption);
                        setFieldValue("customerFullName", "");
                        setFieldValue("outlet", []);
                        setCustomerOptions([]);
                        setOutletOptions([]);

                        setTouched({
                          ...touched,
                          companyName: false,
                          customerFullName: false,
                        });

                        handleCompanyChange(selectedOption?.value);
                      }}
                    />

                    {/* Customer Name */}
                    <SelectWithLabel
                      idFromName="customerFullName"
                      placeholder="Customer Name*"
                      options={customerOptions}
                      value={values?.customerFullName?.value || ""}
                      setFieldValue={setFieldValue}
                      helperText={
                        !values?.companyName ? "Select a company first" : ""
                      }
                      errors={errors.customerFullName}
                      touched={touched.customerFullName}
                      endAdornment={
                        values?.customerFullName ? (
                          <IconButton
                            sx={{ position: "relative", right: "10px" }}
                            onClick={() => {
                              setFieldValue("customerFullName", "");
                            }}
                          >
                            <ClearIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        ) : null
                      }
                      onChange={(event) => {
                        const selectedOption = customerOptions.find(
                          (option) => option.value === event.target.value
                        );
                        setFieldValue("customerFullName", selectedOption);
                      }}
                    />
                    {/* Outlet */}
                    <SelectWithLabel
                      idFromName="outlet"
                      placeholder="Outlets"
                      options={outletOptions}
                      value={
                        values?.outlet?.map((option) => option.value) || []
                      }
                      helperText={
                        !values?.companyName ? "Select a company first" : ""
                      }
                      setFieldValue={setFieldValue}
                      multiple
                      endAdornment={
                        Array.isArray(values?.outlet) &&
                        values?.outlet.length > 0 ? (
                          <IconButton
                            sx={{
                              position: "relative",
                              right: "10px",
                            }}
                            onClick={() => {
                              setFieldValue("outlet", []);
                              // Commented out: Auto-clear remark when clearing outlets
                              // setFieldValue("remark", "");
                            }}
                          >
                            <ClearIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        ) : null
                      }
                      onChange={(event) => {
                        const selectedValues = event.target.value;
                        const selectedOptions = outletOptions.filter((option) =>
                          selectedValues.includes(option.value)
                        );
                        setFieldValue("outlet", selectedOptions);
                        
                        // Commented out: Auto-populate remark with selected outlets
                        // if (selectedOptions.length > 0) {
                        //   const outletText = selectedOptions
                        //     .map((option) => `${option.label} - `)
                        //     .join("\n");
                        //   setFieldValue("remark", outletText);
                        // } else {
                        //   setFieldValue("remark", "");
                        // }
                      }}
                      renderValue={(selected) => (
                        <>
                          {selected.map((value) => {
                            const selectedOption = outletOptions?.find(
                              (option) => option.value === value
                            );
                            return (
                              <Chip
                                key={value}
                                label={selectedOption?.label}
                                sx={{ marginRight: "5px" }}
                                clickable
                                variant="outlined"
                                deleteIcon={
                                  <Cancel
                                    onMouseDown={(event) =>
                                      event.stopPropagation()
                                    }
                                  />
                                }
                                onDelete={(e) =>
                                  handleDeleteOutlet(
                                    e,
                                    value,
                                    values,
                                    setFieldValue
                                  )
                                }
                              />
                            );
                          })}
                        </>
                      )}
                    />

                    {/* Technician */}
                    <SelectWithLabel
                      idFromName="technician"
                      placeholder="Technician"
                      options={techniciansOptions}
                      value={
                        values?.technician?.map((option) => option.value) || []
                      }
                      multiple
                      setFieldValue={setFieldValue}
                      endAdornment={
                        Array.isArray(values?.technician) &&
                        values?.technician.length > 0 ? (
                          <IconButton
                            sx={{
                              position: "relative",
                              right: "10px",
                            }}
                            onClick={() => setFieldValue("technician", [])}
                          >
                            <ClearIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        ) : null
                      }
                      onChange={(event) => {
                        const selectedValues = event.target.value;
                        const selectedOptions = techniciansOptions.filter(
                          (option) => selectedValues.includes(option.value)
                        );
                        setFieldValue("technician", selectedOptions);
                      }}
                      renderValue={(selected) => (
                        <>
                          {selected.map((value) => {
                            const selectedOption = techniciansOptions?.find(
                              (option) => option.value === value
                            );
                            return (
                              <Chip
                                sx={{ marginRight: "5px" }}
                                key={value}
                                label={selectedOption?.label}
                                variant="outlined"
                                onDelete={(e) =>
                                  handleDeleteTechnician(
                                    e,
                                    value,
                                    values,
                                    setFieldValue
                                  )
                                }
                                deleteIcon={
                                  <Cancel
                                    onMouseDown={(event) =>
                                      event.stopPropagation()
                                    }
                                  />
                                }
                              />
                            );
                          })}
                        </>
                      )}
                    />
                    {/* Phone */}
                    <div>
                      <InputWithLabel
                        className={
                          "text-black  border hover:border-themeBtn border-borderTheme/80 focus-within:border-themeBtn  transition duration-300 ease-in-out"
                        }
                        idFromName="contactNumber"
                        label="Contact Number"
                        type={"number"}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.contactNumber}
                        onFocus={(e) =>
                          e.target.addEventListener(
                            "wheel",
                            function (e) {
                              e.preventDefault();
                            },
                            { passive: false }
                          )
                        }
                      />
                    </div>
                    {/* Email */}
                    <InputWithLabel
                      className={
                        "text-black hover:border-themeBtn border border-borderTheme/80 focus-within:border-themeBtn transition duration-300 ease-in-out "
                      }
                      idFromName="email"
                      label="Email"
                      type="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                    />
                  </div>
                </div>

                {/* Second Form */}
                <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mt-4">
                  <h2 className="text-base pb-6 font-normal text-textSecondary">
                    Recurring Information
                  </h2>
                  <div className="grid grid-cols-3 gap-6 mb-4">
                    <div>
                      <InputWithLabel
                        className={`text-black border ${
                          errors.quantity && touched.quantity
                            ? "border-red"
                            : "border-borderTheme/80 focus-within:border-themeBtn"
                        } transition duration-300 ease-in-out`}
                        idFromName="quantity"
                        label="Quantity*"
                        type="number"
                        onBlur={handleBlur}
                        onChange={(e) =>
                          setFieldValue("quantity", e.target.value)
                        }
                        onFocus={(e) =>
                          e.target.addEventListener(
                            "wheel",
                            function (e) {
                              e.preventDefault();
                            },
                            { passive: false }
                          )
                        }
                        value={values.quantity || ""}
                        errors={errors.quantity && touched.quantity}
                        errorsText={errors.quantity}
                      />
                    </div>
                    <div>
                      <InputWithLabel
                        className={`text-black border ${
                          errors.pricing && touched.pricing
                            ? "border-red"
                            : "border-borderTheme/80 focus-within:border-themeBtn"
                        } transition duration-300 ease-in-out`}
                        idFromName="pricing"
                        label="Pricing*"
                        type="number"
                        onBlur={handleBlur}
                        onFocus={(e) =>
                          e.target.addEventListener(
                            "wheel",
                            function (e) {
                              e.preventDefault();
                            },
                            { passive: false }
                          )
                        }
                        onChange={(e) =>
                          setFieldValue("pricing", e.target.value)
                        }
                        value={values.pricing || ""}
                        errors={errors.pricing && touched.pricing}
                        errorsText={errors.pricing}
                      />
                    </div>
                    <div>
                      <SelectWithLabel
                        idFromName="duration"
                        placeholder="Cycle Duration"
                        options={itemOptions}
                        value={values?.duration?.value ?? values?.duration}
                        setFieldValue={setFieldValue}
                        errors={errors.duration}
                        touched={touched.duration}
                        endAdornment={
                          values?.duration ? (
                            <IconButton
                              sx={{
                                position: "relative",
                                right: "10px",
                              }}
                              onClick={() => setFieldValue(`duration`, "")}
                            >
                              <ClearIcon sx={{ fontSize: "16px" }} />
                            </IconButton>
                          ) : null
                        }
                        onChange={(event) => {
                          const selectedOption = itemOptions.find(
                            (option) => option.value === event.target.value
                          );
                          setFieldValue("duration", selectedOption);
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* summary */}
                {values?.quantity && values?.pricing && (
                  <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mt-4">
                    <div>
                      <h2 className="text-xs pb-4 font-normal text-primaryBlack">
                        Summary:
                      </h2>
                    </div>
                    <div>
                      <div className="flex">
                        <p className="text-sm text-textSecondary">
                          Total Outlet:
                        </p>
                        <p className="text-sm text-textSecondary ml-1">
                          {Array.isArray(values?.outlet)
                            ? values.outlet
                                .map((outlet) => outlet.label)
                                .join(", ")
                            : values?.outlet?.label || "No Outlet Selected"}
                        </p>
                      </div>
                      <div className="flex">
                        <p className="text-sm text-textSecondary">
                          Total Servicing Count:
                        </p>
                        <p className="text-sm text-textSecondary ml-1">
                          {values?.quantity || 0}
                        </p>
                      </div>
                      <div className="flex">
                        <p className="text-sm text-textSecondary">Duration:</p>
                        <p className="text-sm text-textSecondary ml-1">
                          {values?.duration?.label || values?.duration} Renewal
                          Period
                        </p>
                      </div>
                      <div className="flex">
                        <p className="text-sm text-textSecondary">
                          Total Price:
                        </p>
                        <p className="text-sm text-themeBtn ml-1">
                          ${(Number(values?.quantity) || 0) * (Number(values?.pricing) || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Remark */}
                <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mt-4">
                  <div>
                    <h2 className="text-xs pb-4 font-normal text-mgrey">
                      Remarks(optional)
                    </h2>
                  </div>
                  <div>
                    <InputWithLabel
                      className={`text-black border ${
                        errors.remark && touched.remark
                          ? "border-red"
                          : "border-borderTheme/80 focus-within:border-themeBtn"
                      } transition duration-300 ease-in-out`}
                      idFromName="remark"
                      label="Description"
                      type="textarea"
                      rows={3}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.remark || ""}
                      style={{ minHeight: "120px" }}
                      onInput={(e) => {
                        e.target.style.height = "auto";
                        e.target.style.height = `${e.target.scrollHeight}px`;
                      }}
                    />
                  </div>
                </div>
                {/* button */}
                <div className="flex justify-end gap-3 mt-3">
                  <DefaultPrimaryButton
                    type="button"
                    value="Cancel"
                    onClick={() => navigate("/services")}
                  />

                  <>
                    <DefaultButton type="submit" value="Save" />
                  </>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
      {showConfirmationModal && (
        <div>
          <ConfirmationModel
            confirmText={isEditMode === "edit" ? "Edit" : "Create"}
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={() => handleConfirm(formValues)}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default CreateEditRecurringService;
